import { Modal } from "antd";
import React, { useEffect, useState, useContext } from "react";
import uniqBy from "lodash/uniqBy";
import orderBy from "lodash/orderBy";
import { isString } from "lodash";
import dayjs from "dayjs";
import { UserContext } from "../amplify/authenticator-provider";
import { getEffectiveCognitoRoles } from "../hooks/utils";
import { closeModal, openModal, drawerClose, drawerOpen, drawerUpdate } from "./rxjsSubjects";

export const formatDate = dateString =>
  new Date(dateString).toLocaleString("us-EN", {
    month: "numeric",
    year: "numeric",
    day: "numeric",
  });

export const showConfirmationModal = (title, content, cb) => {
  Modal.confirm({
    title,
    content,
    okType: "danger",
    onOk() {
      cb();
    },
    onCancel() {},
  });
};

// filters specific
export const filterByType = data => type => {
  if (!data) return [];
  return data.filter(item => item.type === type);
};

export const filterByTypeName = data => typeName => {
  if (!data) return [];
  return data.filter(item => item.typeName === typeName);
};

export const capitalize = string => String(string).charAt(0).toUpperCase() + string?.slice(1);

/**
 * Extracts the path portion from a full URL, ignoring the domain
 * @param {string} fullUrl - The complete URL (e.g., "https://app.engagedai.io/engagements/activity-center/12102012926?loc=364")
 * @returns {string} - The path portion (e.g., "/engagements/activity-center/12102012926?loc=364")
 */
export const extractUrlPath = fullUrl => {
  if (!fullUrl || typeof fullUrl !== "string") {
    return fullUrl || "";
  }

  // If it's already a relative path, return as is
  if (!fullUrl.includes("://")) {
    return fullUrl;
  }

  try {
    const url = new URL(fullUrl);
    return url.pathname + url.search + url.hash;
  } catch (error) {
    console.error("Error parsing URL:", error);
    // Fallback: return original URL if parsing fails
    return fullUrl;
  }
};

const GRAPHQL_ERROR = "GraphQL error: ";
export const displayGraphQLError = e =>
  e?.message ? e?.message.replace(GRAPHQL_ERROR, "") : "Unexpected Error";

/**
 * Generic function to parse JSON string or return object/array as is
 * @param {string|object|array} data - Data as JSON string, object, or array
 * @param {object|array} defaultValue - Default value to return if parsing fails or data is null/undefined (defaults to {})
 * @param {string} errorContext - Optional context message for error logging
 * @returns {object|array} Parsed data or default value
 */
export const parseJSONData = (data, defaultValue = {}, errorContext = "data") => {
  if (!data || data === null) {
    return defaultValue;
  }

  try {
    return isString(data) ? JSON.parse(data) : data || defaultValue;
  } catch (error) {
    console.error(`Error parsing ${errorContext}:`, error);
    return defaultValue;
  }
};

export const usePersistedState = (key, defaultValue) => {
  const [state, setState] = useState(() =>
    JSON.parse(sessionStorage.getItem(key) || JSON.stringify(defaultValue))
  );
  useEffect(() => {
    sessionStorage.setItem(key, JSON.stringify(state));
  }, [key, state]);
  return [state, setState];
};

export const filterAndSortingData = (data, sortBy, filter = null) => {
  let localData = uniqBy(data || [], sortBy);
  if (filter) {
    localData = localData.filter(el => el[filter?.key] === filter?.value);
  }
  localData = orderBy(localData, [sortBy], ["asc"]).map(el => ({
    id: el[sortBy],
    name: el[sortBy],
  }));
  return localData;
};

export const getVehicleYears = () => {
  const years = [];
  for (let i = 1995; i <= dayjs().add(1, "years").year(); i++) {
    years.push(i);
  }
  return years;
};

export const checkAccessPermission = key => {
  const { user, authData } = useContext(UserContext);
  const accessPayload = authData?.signInUserSession?.accessToken?.payload;
  let access;
  // console.log("helpers:checkAccessPermission:accessPayload", accessPayload);
  // console.log("helpers:checkAccessPermission:getCognitoRoles", getCognitoRoles(accessPayload));
  // EAI-1486: while impersonating, do NOT grant blanket admin access — fall
  // through to the target's accessControlPermissions so gated UI matches what
  // the target user can actually do (the backend enforces this regardless).
  if (getEffectiveCognitoRoles(accessPayload)[0] === "ADMIN") {
    access = true;
  } else if (user && user.accessControlPermissions?.length > 0 && key) {
    const canAccess = user.accessControlPermissions.findIndex(el => el === key);
    access = canAccess > -1;
  }
  return access;
};

export const hasAdminAccess = () => {
  try {
    const { authData } = useContext(UserContext);
    const accessPayload = authData?.signInUserSession?.accessToken?.payload;
    return getEffectiveCognitoRoles(accessPayload)[0] === "ADMIN";
  } catch (err) {
    console.log("ERROR:", err);
  }
  return false;
};

// for open dialog
export const openDialog = (...params) => {
  openModal.next([...params]);
};

// for close dialog
export const closeDialog = (...params) => {
  closeModal.next(...params);
};

// for open drawer
export const openDrawer = (...params) => {
  drawerOpen.next([...params]);
};

// for close drawer (closes last drawer in stack)
export const closeDrawer = () => {
  drawerClose.next();
};

// for updating drawer content (updates component props of current drawer)
export const updateDrawer = (newComponentProps, forceRemount = false) => {
  drawerUpdate.next({ props: newComponentProps, forceRemount });
};

// scroll to top
export const handleScrollToTop = position => {
  document.documentElement.scrollTop = position || 0;
};

// scroll to bottom
export const handleScrollToBottom = () => {
  document.documentElement.scrollTo = document.documentElement.scrollHeight;
};

export const validateUserInputs = user => {
  return user.firstName.trim() !== "" && user.lastName.trim() !== "";
};

export const isEmailValid = email => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};

export const getLocationObj = user => {
  try {
    const locationId = sessionStorage.getItem("currentLocationId");
    if (locationId.includes(",")) {
      return user.location[0];
    }
    const obj = user?.locations?.find(item => +item.location.id === +locationId);
    return obj;
  } catch (err) {
    console.log(err);
  }
};

export const getResourceSize = async url => {
  try {
    const res = await fetch(url, { method: "HEAD" });
    if (res.ok) {
      const contentLength = res.headers.get("Content-Length");
      if (contentLength) {
        return parseInt(contentLength, 10);
        // eslint-disable-next-line no-else-return
      } else {
        throw new Error("Content-Length could not found");
      }
    } else {
      throw new Error(`Failed to fetch resource. Status: ${res.status}`);
    }
  } catch (err) {
    console.log(err);
  }
};

export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
};

export const formatSecondsToHHMMSS = seconds => {
  return dayjs().startOf("day").add(seconds, "second").format("HH:mm:ss");
};

export const checkLocation = locationId => {
  let locations = [];
  if (sessionStorage.getItem("currentLocationId")?.includes(",")) {
    locations = sessionStorage
      .getItem("currentLocationId")
      .split(",")
      .map(loc => +loc);
  } else {
    locations = [+sessionStorage.getItem("currentLocationId")];
  }
  return locations.includes(+locationId);
};

export const openUrlInTab = (url, target = "_blank", ignoreFeature = false) => {
  window.open(url, target, ignoreFeature ? undefined : "noopener,noreferrer");
};

export const TitleCase = str => {
  if (!str) return "";
  return str
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * Reusable function to copy text to clipboard and show AntD message.
 * Uses a textarea fallback for browsers that do not support navigator.clipboard.
 * The textarea is only needed for the fallback (older browsers).
 */
export const handleCopyToClipboard = async (
  text,
  message,
  successMsg = "Copied!",
  errorMsg = "Failed to copy!"
) => {
  let ok = false;
  if (!navigator?.clipboard) {
    // Fallback: textarea is needed to select and copy text programmatically
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "absolute";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      ok = document.execCommand("copy");
      document.body.removeChild(textarea);
    } catch {
      ok = false;
    }
  } else {
    // Modern browsers: use clipboard API directly, no textarea needed
    try {
      await navigator.clipboard.writeText(text);
      ok = true;
    } catch {
      ok = false;
    }
  }
  if (ok) {
    message.success(successMsg);
  } else {
    message.error(errorMsg);
  }
};

/**
 * Normalize a URL by ensuring it has a protocol.
 * @param {string} url - The URL to normalize.
 * @returns {string} - The normalized URL.
 */
export const normalizeUrl = url => {
  if (!url) return url;
  const hasProtocol = /^(https?:)?\/\//i.test(url);
  return hasProtocol ? url : `https://${url}`;
};

export const isAllLocationSelected = () => {
  const allLocationIds = sessionStorage.getItem("currentLocationId") || "";
  return allLocationIds.includes(",");
};

/**
 * Get query parameters from the URL
 * @param {string} key - Optional. If provided, returns the value of that specific param. If not provided, returns all params as an object
 * @param {string} url - Optional. If provided, parses params from this URL. Otherwise uses window.location.search
 * @returns {string|object|null} - Returns the param value (string), all params (object), or null if not found
 * @example
 * // URL: ?name=John&age=25&city=NYC
 * getQueryParams() // Returns { name: 'John', age: '25', city: 'NYC' }
 * getQueryParams('name') // Returns 'John'
 * getQueryParams('email') // Returns null
 */
export const getQueryParams = (key = null, url = null) => {
  try {
    const searchParams = new URLSearchParams(url || window.location.search);

    // If key is provided, return specific param value
    if (key) {
      return searchParams.get(key);
    }

    // Otherwise, return all params as an object
    const params = Array.from(searchParams.entries()).reduce((acc, [paramKey, paramValue]) => {
      acc[paramKey] = paramValue;
      return acc;
    }, {});

    return Object.keys(params).length > 0 ? params : null;
  } catch (err) {
    console.error("Error getting query params:", err);
    return null;
  }
};

/**
 * Build a URL with query parameters appended
 * @param {object} params - Object containing key-value pairs of params to set/update
 * @param {string} url - Optional. URL to append params to. If not provided, uses current page URL
 * @param {boolean} replace - If true, replaces all existing params. If false (default), merges with existing params
 * @param {boolean} updateBrowser - If true, updates browser URL (with pushState). If false (default), just returns the URL
 * @returns {string} - The constructed URL with query parameters
 * @example
 * // Build URL with params (default behavior - just returns URL)
 * setQueryParams({ name: 'John', age: 25 }, '/page') // Returns '/page?name=John&age=25'
 * setQueryParams({ tab: 'profile' }, '/dashboard?existing=value') // Returns '/dashboard?existing=value&tab=profile'
 * setQueryParams({ name: 'John' }, '/page', true) // Returns '/page?name=John' (replaces all params)
 *
 * // Update current page URL (set updateBrowser to true)
 * setQueryParams({ name: 'John' }, null, false, true) // Updates current page URL and returns it
 * setQueryParams({ filter: null }, null, false, true) // Removes 'filter' param and updates browser URL
 */
export const setQueryParams = (params = {}, url = null, replace = false, updateBrowser = false) => {
  try {
    // Determine the base URL and existing search params
    const baseUrl = url || window.location.pathname;
    const existingSearch = url
      ? url.includes("?")
        ? url.split("?")[1].split("#")[0]
        : ""
      : window.location.search;

    // Parse URL components if url contains hash
    let pathname = baseUrl;
    let hash = "";
    if (url && url.includes("#")) {
      [pathname, hash] = url.split("#");
      [pathname] = pathname.split("?");
    }

    // Create URLSearchParams from existing or empty
    const searchParams = new URLSearchParams(replace ? "" : existingSearch);

    // Update or add new params
    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        searchParams.delete(key);
      } else {
        searchParams.set(key, String(value));
      }
    });

    // Construct new URL
    const newUrl = `${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ""}${
      hash ? `#${hash}` : ""
    }`;

    // Update browser URL if requested
    if (updateBrowser) {
      window.history.pushState({}, "", newUrl);
    }

    return newUrl;
  } catch (err) {
    console.error("Error setting query params:", err);
    return url || window.location.href;
  }
};

// Helper to build tree from flat list using parentStatusId, supporting multi-level nesting
export const buildStatusTree = statusList => {
  if (!statusList || statusList.length === 0) return [];

  // Recursive function to map API structure to tree nodes
  const mapNode = node => ({
    id: node.key,
    key: node.key,
    name: node.name,
    isDefault: node.isDefault,
    children: Array.isArray(node.childs) && node.childs.length > 0 ? node.childs.map(mapNode) : [],
  });

  // Top-level nodes
  return statusList.map(mapNode);
};

// Regex for URL matching - defined once outside function for performance
const URL_REGEX = /(https?:\/\/[^\s]+)/g;

/**
 * Renders text content with clickable, properly decoded URLs
 * @param {string} text - The text content that may contain URLs
 * @returns {React.Element|string} - React elements with clickable links or plain text
 * @example
 * // Text with URL
 * renderTextWithLinks("Check this out: https://example.com/page?id%5B0%5D=123")
 * // Returns JSX with clickable, decoded URL
 */
export const renderTextWithLinks = text => {
  if (!text) return "N/A";

  // Split by URL regex - captured groups are included in the result
  const parts = text.split(URL_REGEX);

  return parts.map((part, index) => {
    // Check if this part starts with http:// or https:// (it's a URL)
    // This is faster than regex.test() since we already split by the regex
    if (part.startsWith("http://") || part.startsWith("https://")) {
      // Decode the URL for display (handles %5D, %5B, etc.)
      let decodedUrl = part;
      try {
        decodedUrl = decodeURIComponent(part);
      } catch (error) {
        // If decoding fails, use original URL
        console.warn("Failed to decode URL:", part, error);
        decodedUrl = part;
      }

      return (
        <a
          key={index}
          href={part} // Use encoded URL for href
          target="_blank"
          rel="noopener noreferrer"
          className="!text-twLinkColor underline break-all font-light"
          onClick={e => e.stopPropagation()}
          title={decodedUrl} // Show decoded URL in tooltip
        >
          {decodedUrl}
        </a>
      );
    }
    return <span key={index}>{part}</span>;
  });
};
